export const BOOKMAKER_PRIORITY = ["draftkings", "fanduel", "betmgm"];

export const dedupeOddsGames = (games) => {
  if (!Array.isArray(games) || games.length === 0) return [];

  const byId = new Map();

  for (const game of games) {
    if (!game?.id) continue;

    const existing = byId.get(game.id);
    if (!existing) {
      byId.set(game.id, {
        ...game,
        bookmakers: [...(game.bookmakers || [])],
      });
      continue;
    }

    const seenKeys = new Set(
      (existing.bookmakers || []).map((b) => b?.key).filter(Boolean)
    );

    for (const bookmaker of game.bookmakers || []) {
      if (bookmaker?.key && !seenKeys.has(bookmaker.key)) {
        existing.bookmakers.push(bookmaker);
        seenKeys.add(bookmaker.key);
      }
    }
  }

  return Array.from(byId.values());
};

/**
 * Core markets shown on the board. A book that offers all three lets us fill
 * Spread / M-Line / Total from ONE source; more coverage = a more complete book.
 */
export const CORE_MARKET_KEYS = ["h2h", "spreads", "totals"];

/** How many of the core markets a bookmaker offers (0–3). */
export const countCoreMarkets = (bookmaker) => {
  const markets = bookmaker?.markets;
  if (!Array.isArray(markets)) return 0;
  const keys = new Set(
    markets.map((m) => (m?.key || "").toLowerCase()).filter(Boolean)
  );
  return CORE_MARKET_KEYS.reduce(
    (count, key) => (keys.has(key) ? count + 1 : count),
    0
  );
};

const bookmakerPriorityRank = (bookmaker) => {
  const index = BOOKMAKER_PRIORITY.indexOf((bookmaker?.key || "").toLowerCase());
  return index === -1 ? BOOKMAKER_PRIORITY.length : index;
};

/**
 * Choose the single bookmaker used to render a match on the board.
 *
 * We deliberately show every market (Spread / M-Line / Total) from ONE
 * consistent source rather than stitching markets together across books —
 * mixing sources can surface drastically misaligned lines. Selection order:
 *   1. Most core markets offered (h2h + spreads + totals).
 *   2. Configured BOOKMAKER_PRIORITY rank (agent-preferred providers first).
 *   3. Original feed order (stable tie-break).
 *
 * When no single book offers all three markets, the book with the MOST markets
 * wins, so the board still shows the widest set of offerings from one source.
 */
export const getPreferredBookmaker = (bookmakers = []) => {
  if (!Array.isArray(bookmakers)) return null;

  let best = null;
  let bestCoverage = -1;
  let bestRank = Infinity;

  for (const bookmaker of bookmakers) {
    if (!bookmaker) continue;

    const coverage = countCoreMarkets(bookmaker);
    const rank = bookmakerPriorityRank(bookmaker);

    // Strict comparisons keep the first-seen book on a full tie, preserving
    // original feed order (which the backend has already sorted by priority).
    const isBetter =
      best === null ||
      coverage > bestCoverage ||
      (coverage === bestCoverage && rank < bestRank);

    if (isBetter) {
      best = bookmaker;
      bestCoverage = coverage;
      bestRank = rank;
    }
  }

  return best;
};

export const appendUniqueOddsGames = (prev, next) => {
  const base = dedupeOddsGames(prev);
  const existingIds = new Set(base.map((g) => g.id));
  const additions = dedupeOddsGames(next).filter((g) => !existingIds.has(g.id));
  return [...base, ...additions];
};
